import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { CountedLabel } from './CountedLabel';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';
import { useCategories } from './catalogCategories';
import {
  MadePieceCard, SubStepChips, SPENT_EDGE, availableAfter, chip,
  outputChip, stockFill, usePartLabel,
  type Available, type MadePiece, type OpenPiece, type RowPieces,
} from './SubStepChips';
import { subStepKindTone, subStepKindWords } from './subStepKinds';
import { PART_GROUPS, groupOfCategory, type PartItem } from './partsRun';
import { subStepOutputName, type PieceName, type SubStep } from './stepsRun';
import { renderSubStepText } from './subStepText';
import { useDragScroll } from './useDragScroll';

// EVERY SUB-STEP OF THE MANUAL, ON ONE PAGE — the first position of Phase 4's navigator,
// before 1A.
//
// The phase stands on ONE gesture and steps through them, which is the right shape for the
// question it is built around: what does this sentence say, read against the diagram it came
// from. It is the wrong shape for the other question a reader brings, which is about the
// sequence rather than about any member of it — is anything missing, does 2C repeat what 2A
// said, does the whole of step 3 amount to what step 3 does. That one is answered by reading
// them in a column, and it was not answerable here at all: it meant pressing › thirty times
// and holding the answers in your head.
//
// So the navigator gains a place that is not a sub-step. Nothing else changes: the same list,
// in the same order, under the same control.
//
// A ROW IS NOT A LINK. It was one — press it and the phase stood on that sub-step with its
// diagram and its holes — and that press was in the way of the gesture this table actually
// needs. The table is wider than the page by construction (eight columns, and the chips
// cannot wrap), so it is PULLED sideways, and a pull and a click start identically: every
// reading of the sequence began with the risk of landing on 2C. The navigator above the table
// is how you go to a sub-step, and it is always on screen.
//
// What is still pressable inside a row is the chips, which open what a piece is made of — a
// press on a NAME, which cannot be mistaken for a press on the row, and which the drag
// swallows the trailing click of (see useDragScroll).
//
// FIVE COLUMNS, and the last four are one sentence read across: what the gesture SAYS to do,
// which of the five KINDS it is, what it TAKES, what it LEAVES. That is the order the kinds
// are written in (see subStepKinds), and it is what makes the row checkable — the kind says
// how many components and how much hardware the action may take, the chips say what it
// actually took, and the output says what came out the other side.
//
// The parts column carries the half of an answer the sentence only implies: "using screws (E)
// and spring washers (F)" against E ×2, F ×2, I as a tool. Reading a manual's gestures for
// what they leave out is precisely reading them against that list. Columns, not a second
// page: the point of this place is that the whole sequence is on one.

// `step` is only ever compared — see cycleRuns, where a run is stopped by a step ending.
// `foldsInto`: a hardware prepared in the hand, drawn in the row of the gesture that uses it rather
// than as a row (see foldsOf); `preps`, on that row, what it plays first.
type Row = { key: string; label: string; step: unknown; sub: SubStep; foldsInto?: string | null; preps?: SubStep[] };

// THE GESTURE NAMED IN FULL — "Screwing two component parts together" — in the family's colour.
// It read "COMBINING 5" and kept the sentence in a title attribute, on the reasoning that the
// cell was not the reading and the sentence was too long for it. But a column nobody can read
// without hovering is not a short column, it is an unread one, and the width it saved went to
// a sentence column that already had a ceiling on it.
//
// WRAPPED, NOT NOWRAP, and the column widened to suit: three of the five are four words or
// more, and a label forced onto one line would push the parts and the output off the page for
// every row in the table. Two lines in a cell that is centred anyway costs a row nothing.
//
// A DASH where the kind is null, the same mark the parts column uses and for the same reason:
// an empty cell is unreadable as an answer. This field is absent on every sub-step analysed
// before the five were asked for, so most of the library shows a column of them — which is
// the true state of it, and now that the question has a heading of its own, saying nothing
// under that heading would read as the table being broken rather than as the manual being old.
function KindLabel({ kind }: { kind: SubStep['kind'] }) {
  const { t } = useLanguage();
  // No family fallback here: this table stands on Phase 4's answer, and a row has no movement
  // to read one off. Phase 5 passes one — see subStepKindWords.
  const said = subStepKindWords(kind, null, t);
  if (!said) return <span className="text-sm text-slate-400">—</span>;
  return (
    <span
      className={`inline-block rounded-lg px-2 py-1 text-[11px] font-semibold leading-snug ${subStepKindTone(said.family)}`}
    >
      {said.text}
    </span>
  );
}

// WHAT IS ON THE TABLE WHEN THIS GESTURE IS DONE: the supplied parts on one line, the pieces
// made out of them on the next — amber above violet, which is the order they exist in. What
// the step was given comes first and does not move; what has been built out of it accumulates
// underneath.
//
// TWO LINES AND NOT THREE. The parts columns sort by component / hardware / tool because that
// is what a person reaches for; this cell is asking a different question — what is finished
// and what is still loose — and the answer to that is exactly two piles. Tools are in neither:
// nothing uses one up, so "still available" is true of every tool at every moment and says
// nothing.
//
// A DASH FOR AN EMPTY TABLE, which is what the last row of a well-formed step should show:
// everything the step supplied has gone into something. A blank would read as unanswered.
function AvailableCell({ have, items, pieces }: {
  have?: Available;
  items: PartItem[];
  // Every piece known by this point, so a component of one that is itself a piece can be
  // named and opened in the window.
  pieces?: Map<string, MadePiece>;
}) {
  const partLabel = usePartLabel(items);
  if (!have || (!have.made.length && !have.parts.length)) {
    return <span className="text-sm text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-col items-center gap-1.5">
      {!!have.parts.length && (
        <div className="flex flex-wrap justify-center gap-2">
          {/* SPENT IS DASHED AND FADED, the same mark a chip wears in the parts columns when
              the gesture uses it up — one fact drawn one way, a column apart: there it says
              "this goes into the piece", here it says "and there is none of it left". */}
          {/* A MIRROR PAIR IS TWO LINES, not one line of two — see Available.parts. The left
              side rail and the right one are different objects, so "×2" of them is a count of
              nothing you could pick up, and the key has to carry the hand or React draws one
              chip where the stock has two. */}
          {have.parts.map((p) => (
            <PieceChip
              key={`${p.id}-${p.side ?? ''}`}
              violet={false}
              // THE BORDER OF THE SPENT MARK, AND NOT ITS FADE. The fade is the gauge's job
              // here and it says how much rather than whether — see stockFill, which is also
              // why the words no longer dim with the chip.
              mark={p.n === 0 ? SPENT_EDGE : ''}
              fill={stockFill(p.n, p.of)}
              base={p.id}
              items={items}
              pieces={pieces ?? new Map()}
              text={(
                <>
                  <CountedLabel label={partLabel(p.id, p.side)} n={p.n} />
                </>
              )}
            />
          ))}
        </div>
      )}
      {!!have.made.length && (
        <div className="flex flex-wrap justify-center gap-2">
          {have.made.map((m) => (
            <PieceChip
              key={m.id}
              violet
              piece={m}
              items={items}
              pieces={pieces ?? new Map()}
              text={(
                <>
                  <CountedLabel label={`${m.id} - ${m.name}`} n={m.n} />
                </>
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// THE PIECE THIS GESTURE LEAVES, in the same violet chip it will wear when a later sub-step
// picks it up — one object, drawn one way, at the two moments it appears.
//
// THREE STATES, and the blank is the interesting one. A PLACING leaves nothing, so its cell
// is empty and that emptiness is the answer: it is the one blank in this table that is not a
// missing answer, and a dash there would claim a question the kind rules out. A COMBINING
// with no name gets the dash, because that one IS missing — every sub-step analysed before
// the field was asked for lands here until its step is re-run. And a sub-step of no known
// kind gets nothing, since there is nothing to say either way.
// A CHIP THAT SAYS WHAT IT IS MADE OF, for the two places in this table that draw their own
// rather than going through SubStepChips: the OUTPUT column and the stock column. Same window,
// same one level down — see MadePieceCard, where the rule is argued.
function PieceChip({ text, piece, base, items, pieces, violet, mark = '', fill }: {
  text: ReactNode;
  piece?: MadePiece;
  base?: string;
  items: PartItem[];
  pieces: Map<string, MadePiece>;
  violet: boolean;
  // Whatever the caller draws ON the chip besides its colour — spent, in the stock column.
  mark?: string;
  // AND THE COLOUR ITSELF, where the caller has something to say with it. One caller has: the
  // stock column fills the chip to the share of the part still on the table (see stockFill).
  // A style and not a class, because the stop is a percentage computed per chip and there is
  // no such thing as a class for 3/4.
  fill?: { background: string };
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState<OpenPiece | null>(null);
  const mine: OpenPiece | null = piece
    ? { kind: 'made', piece }
    : base != null ? { kind: 'base', id: base } : null;
  return (
    <>
      {open && (
        <MadePieceCard
          open={open}
          items={items}
          pieces={pieces}
          onClose={() => setOpen(null)}
        />
      )}
      <button
        type="button"
        // Stopped: the row is a control of its own, and it walks the navigator away from
        // the window this press is opening.
        onClick={(e) => { e.stopPropagation(); if (mine) setOpen(mine); }}
        title={t('notice.steps.madeOfOpen')}
        style={fill}
        className={`${violet ? outputChip : chip} ${mark} cursor-pointer hover:opacity-80`}
      >
        {text}
      </button>
    </>
  );
}

function OutputLabel({ sub, piece, items, held }: {
  sub: SubStep;
  piece?: MadePiece;
  items: PartItem[];
  // What this gesture was already holding, so a placing that moves a piece an earlier gesture
  // MADE draws it as that piece. Without it the cell resolves "114" against the parts list,
  // finds nothing, and prints "#114" in a supplied part's amber — the one thing on the row
  // that is certainly not a supplied part.
  held?: Map<string, MadePiece>;
}) {
  const { language } = useLanguage();
  // ONE CELL FOR EVERY KIND. It had a branch of its own for a placing, which drew first the
  // parts unchanged and then a piece this app invented for it — both of them workarounds for a
  // gesture that answered "output": null. It answers with a name now, like every other, and
  // there is one thing to draw.
  const name = subStepOutputName(sub.output, language);
  if (!name) return <span className="text-sm text-slate-400">—</span>;
  // NAMED WITH ITS ID, "101 - Prepared screw" — an id of its own, in the hundreds where no
  // supplied part reaches, and the same string a later gesture picks it up by. See madePieces,
  // which assigns them: this cell does not invent one, it prints the one that was assigned.
  return (
    <PieceChip
      violet
      piece={piece}
      items={items}
      pieces={held ?? new Map()}
      text={(
        <>
          {`${piece?.id ?? '?'} - ${name}`}

        </>
      )}
    />
  );
}


// THE TABLE'S CARD, exported for the empty one drawn in its place while the first sub-steps
// are still being read (see MergedStep).
export const SUB_TABLE_CARD = 'w-full rounded-xl border-2 border-slate-300 bg-white shadow-sm '
  + '';

export function AllSubSteps({
  rows, all = rows, items, made, pieces, carded = false,
}: {
  rows: Row[];
  // THE WHOLE MANUAL, against which this step's stock is worked out — the same two lists
  // AssemblyTree takes, and for the same reason. A step's table is drawn from `rows`, and
  // what is ON THE TABLE during it cannot be: a piece assembled in step 1 and picked up in
  // step 2 is only knowable to a walk that saw step 1.
  //
  // Handed one step's rows for both, availableAfter could not find the piece that gesture
  // names — it is in no row it was given — so it brought nothing in, and a step that works on
  // what an earlier one built showed an empty stock beside a gesture consuming it. The
  // overview, which is given every row, was right the whole time, which is what made it look
  // like a numbering problem rather than a scope one.
  all?: Row[];
  // WHAT EACH GESTURE HOLDS AND WHAT IT LEAVES, built by the caller — the single sub-step page
  // needs the same answer, and Phase 5 numbers its pieces from the same sequence. Built twice
  // it would be two walks over one list, and the day either changed, two numberings.
  made: Map<string, RowPieces>;
  // THE PIECES BY ID, for the sentence. It names its parts by id in braces now and is drawn
  // out of the same list the chips are (see subStepText), so it needs what resolves a piece —
  // the caller's, like everything else here that is a fact about the whole manual.
  pieces: Map<string, PieceName>;
  // The supplied parts, to name the ids each sub-step answers in — the chips are the
  // sub-step page's own, and they need what it needs.
  items: PartItem[];
  // ALREADY IN A CARD (a TableCard, titled): no card of its own round it, which would be a second
  // border inside the first.
  carded?: boolean;
}) {
  const { language } = useLanguage();
  const st = useStableText();
  const categories = useCategories();

  // The language on screen, with the other as a fallback for a sub-step that only came back in
  // one.
  const said = (sub: SubStep) => renderSubStepText(
    language === 'fr' ? sub.textFr || sub.textEn : sub.textEn || sub.textFr,
    sub.parts,
    items,
    pieces,
    language,
  );


  // WHAT EACH PART HAS ALREADY BECOME, walked in the manual's order: every part a combining
  // sub-step uses up is mapped to the name that sub-step gave what it left. A later gesture
  // listing that part as merely handled is holding the assembled piece, not the loose part —
  // that is the prompt's own rule for a piece with no id — so the chips draw it as one violet
  // output instead of as the ghosts of the parts it swallowed.
  //
  // BUILT HERE because only this component holds the whole sequence; a sub-step cannot know
  // what the sub-steps before it left. Later entries win: a part built into a bigger piece
  // twice over is, from then on, the bigger piece.
  const madeSoFar = made;
  // AND WHAT IS LEFT AFTER EACH ONE — the same walk, spending instead of naming. See
  // availableAfter, which is where the step's own stock is worked out.
  // FURNITURE OR NOT, off the ontology — the same question the chips' own grouping asks, and
  // the same answer, so a part flagged as handled several at once is one the COMPONENT PARTS
  // column is showing.
  const isComponent = useCallback(
    (p: PartItem | undefined) =>
      !!p && !p.tool && groupOfCategory(p.category, categories) === PART_GROUPS[0],
    [categories],
  );
  const onHand = useMemo(
    () => availableAfter(all, items, madeSoFar, isComponent),
    [all, items, madeSoFar, isComponent],
  );

  // Header and body share the rule, so the grid is one weight throughout — the movement bar's
  // tables are built the same way and for the same reason.
  //
  // CENTRED, EVERY COLUMN, which is what the rest of this phase does: the sub-step's own
  // sentence is centred where it is read one at a time, and a column of left-aligned prose
  // beside a centred one reads as a table that could not decide. Long sentences wrap to two
  // lines and stay centred, as they do a screen away.
  // AND CENTRED THE OTHER WAY TOO. Top-aligned was right when every cell held one line; the
  // chips column can hold three, so a row is now as tall as its tallest cell and a sentence
  // pinned to the top of it floats above nothing. Middle puts each cell's content on the
  // row's own axis, which is what a reader takes a row to be.
  // GRAB THE TABLE AND PULL. The columns take their content's width, so a manual with long
  // output names is wider than the page — and the honest ways out of that are all worse:
  // shrink the chips until they wrap (which is what looked cut), or hide columns behind a
  // scrollbar macOS does not draw until something is already scrolling.
  //
  // THE APP'S OWN HOOK, not a copy of it. This file had its own — written when a row was a
  // link and the drag had to hand the row a way to tell a pull from a press. Nothing needs
  // telling now, and what is left is the shared hook's job exactly: pull past four pixels and
  // the container scrolls, and the click the browser fires at the end of a pull is swallowed
  // before it reaches a chip.
  const pan = useDragScroll<HTMLDivElement>();
  const cell = 'border border-slate-200 px-3 py-2 text-center align-middle';
  // THE HEADINGS IN THE PAGE'S OWN INK, not the grey they were. Seven columns is enough that a
  // reader coming back to the table re-reads the headings to place themselves, and a heading
  // quieter than every cell under it is one they have to hunt for. Ink on light, white on dark.
  const head = `${cell} text-[11px] font-semibold uppercase tracking-wide text-slate-900`;

  return (
    // AS WIDE AS IT NEEDS TO BE, and dragged sideways when that is wider than the page.
    //
    // No min-width on the table, and none is needed: a chip wraps only down to its own words
    // (see `nowrap` in SubStepChips), so a column of them has a min-content width that cannot
    // be squeezed past its longest word, and a table whose
    // min-content outgrows its box overflows it rather than compressing — which is what this
    // container is here to scroll. `min-w-max` would have done it too and cost the width cap
    // on the sentence column: max-content means nothing wraps, so the sentences would each
    // take one very long line and the w-96 below would be a preference nobody honoured.
    //
    // DRAGGED rather than scrolled, because a trackpad's horizontal scroll is a gesture people
    // do not think to try on a table, and macOS draws no scrollbar until one does. Grab it
    // anywhere and pull — anywhere, now that no row is waiting to be pressed instead.
    <div
      ref={pan}
      // A CARD, not the page. This table's ink was chosen for a light surface under it and
      // it had none — no `bg-white` anywhere on the component — so on the blueprint it was
      // dark text on dark blue, invisible but for the bordered chips. Same white fill and
      // card border as every other table in the app; no window chrome on top of it, which is
      // a decoration this one does not want.
      className={`${carded ? 'w-full' : SUB_TABLE_CARD} cursor-grab overflow-x-auto active:cursor-grabbing`}
    >
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {/* WIDE ENOUGH FOR ITS OWN HEADING, and told not to break it: "SUB-STEP" wrapped
                after the hyphen and sat over the ids as two lines of a word, which reads as
                two headings over one column. */}
            <th scope="col" className={`${head} w-24 whitespace-nowrap`}>
              {st('notice.steps.colSub')}
            </th>
            {/* THE SENTENCE, AND A CEILING ON IT. It had no width and so took everything the
                other four did not, which on a wide screen is half the table for one column —
                and a line of prose that long is harder to read, not easier. Bounded here, it
                wraps to two lines and the width it gives up goes to the chips beside it, which
                are the columns that cannot shrink. */}
            {/* A FLOOR AND A CEILING, neither of them a preference. `w-64` alone is a hint an
                auto-laid table is free to ignore, and it did: the chip columns cannot shrink —
                every chip is as wide as its name, up to 14rem — so their min-content width is
                absolute and the only column left to squeeze is the one that CAN wrap. The
                sentence ended up three words wide, set as a tall thin ribbon.

                So a min-width makes the sentence's claim as hard as the chips'. And a
                max-width keeps it from taking what it does not need: at 26rem a sub-step sat
                on one long line, which is a quarter of the table's width spent on the one
                column that reads perfectly well over two or three. Everything to the right of
                it — the chips, the stock — is what wanted that room. */}
            <th scope="col" className={`${head} w-64 min-w-[15rem] max-w-[17rem]`}>
              {st('notice.steps.colSays')}
            </th>
            {/* WHICH OF THE FIVE THE GESTURE IS, between the sentence and the parts, because
                it is the claim those two are checked against: the kind says how many
                components and how much hardware the action may take, and the chips to its
                right say what it actually took. A row that says it screws two component parts
                together, beside two verbs and four parts, is the split that did not happen,
                and the three columns read
                left to right are what shows it.

                Wide enough for two lines of the longest of the five, and allowed to break:
                the labels are sentences now, not "COMBINING 5", and the one column that must
                not wrap is the chips. The heading itself is one short word. */}
            <th scope="col" className={`${head} w-44`}>
              {st('notice.steps.colKind')}
            </th>
            {/* WHAT EACH GESTURE HAS IN HAND, beside what it says to do. The sentence names
                its parts in prose — "using screws (E) and spring washers (F)" — and prose is
                exactly where a missing part hides: a reader checking that a step's units are
                all accounted for has to re-parse fourteen sentences to find out. The chips
                are the answer as DATA, which is what the sub-step was actually asked for, and
                the two disagreeing is the thing these columns are for showing.

                THREE COLUMNS, NOT ONE CELL RULED INTO THREE. It was a single PARTS & TOOLS
                column whose cell drew a line between the groups — the table's own grid faked
                in the one place the grid could not reach — and the bands only lined up within
                a row: the hardware of one gesture sat beside the components of the next
                wherever a group above it was empty. As columns they line up down the whole
                table, they carry a heading each instead of relying on their order to be read,
                and the question "does any gesture in this step use a tool" is answered by
                looking down one column rather than at fourteen cells.

                NO WIDTHS. A chip is at most 14rem and wraps inside that, so each column's
                min-content is its widest chip and there is nothing to prefer; what is left over goes to the
                sentence, which is the column that wants it. */}
            <th scope="col" className={head}>{st('notice.steps.colComponents')}</th>
            <th scope="col" className={head}>{st('notice.steps.colHardware')}</th>
            <th scope="col" className={head}>{st('notice.steps.colTools')}</th>
            {/* WHAT IT LEAVES, last, so the row reads in the order the kinds themselves are
                written: what it says, which kind, what it takes, what it makes. A placing
                shows nothing here — it fastens nothing, so there is no piece — which is a
                blank with a meaning, and the one blank in this table that is not a missing
                answer. */}
            <th scope="col" className={`${head} w-40`}>{st('notice.steps.colOutput')}</th>
            {/* WHAT IS STILL ON THE TABLE once this gesture is done — the step's own stock,
                spent down gesture by gesture, with the pieces the gestures have made added to
                it. Last, because it is the state the NEXT row starts from: the row reads what
                it says, what kind it is, what it takes, what it leaves, and then what is left.

                It answers the question the other columns can only be added up to answer: a
                reader checking that a step accounts for its parts had to hold six totals in
                their head down fourteen rows. Here the last row of a step says it outright,
                and a part still standing there at the end is a part nothing used. */}
            {/* AND THE WIDTH TO SAY IT IN THREE LINES. This column holds the whole step's
                remaining stock — eight or nine chips by the end of a step — and with no width
                of its own it took the narrowest it could and stacked them six deep, which set
                the height of every row in the table. Nothing else in the row is six lines
                tall, so five of those lines were blank in every other column.

                A floor of 42rem fits three chips to a line for all but the longest names, so
                the stock reads as three lines and the rows come down to the height of the
                thing that is actually being read. What does not fit goes to the horizontal
                drag, like the sentence column above. */}
            <th scope="col" className={`${head} min-w-[42rem]`}>
              {st('notice.steps.colAvailable')}
            </th>
          </tr>
        </thead>
        <tbody>
          {/* THE ROWS, IN THE MANUAL'S ORDER, and nothing between them. They were grouped into
              runs — a banner over each, a green dashed ring round the ones that repeated — and
              that went with the cycles. A gesture is one gesture now; there is no run for a
              banner to name. */}
          {rows.filter((r) => !r.foldsInto).map(({ key, label, sub, preps }) => {
                return (
                <tr
                  key={key}
                  // LIT, BUT NOT PRESSABLE. The band follows the pointer because a row is eight
                  // columns long and wider than the page, and tracking one across a pull is the
                  // whole difficulty of reading this table. Slate and not the blue it was: blue
                  // is this app's colour for a thing that can be pressed, and this row cannot —
                  // it has neither the cursor nor the handler any more.
                  className="transition-colors hover:bg-slate-50"
                >
                  {/* The id in the navigator's own type: tabular, semibold, the way "1A" is
                      drawn in the control this row jumps to. Nothing under it — the kind stood
                      there as a second line for a while, and a fact about the gesture filed
                      under its ADDRESS is a fact nobody scans a column of. It has its own
                      column now, beside the sentence it qualifies. */}
                  <td className={`${cell} text-sm font-semibold tabular-nums text-slate-800`}>
                    {label}
                  </td>
                  <td className={`${cell} text-sm text-slate-700`}>
                    {[...(preps ?? []), sub].map(said).filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className={`${cell}`}>
                    <KindLabel kind={sub.kind} />
                  </td>
                  {/* A DASH WHERE A GESTURE NEEDS NOTHING OF THIS GROUP, rather than an empty
                      cell. A blank is unreadable as an answer: it is equally what a sub-step
                      read before parts were asked for looks like, and what an empty column
                      looks like. Most rows now carry two of them — a placing has no hardware
                      and no tool — and that is the point: "this gesture needs no tool" is an
                      answer Phase 4 gives, and a column of dashes broken by one wrench is
                      where the tool actually is.

                      DRAWN BY THE CHIPS THEMSELVES, from `empty`. Which group a part falls in
                      is SubStepChips' answer — the ontology's, really — and a cell that
                      decided for itself whether to draw a dash would be a second opinion on
                      it: one that says "nothing here" over a chip, or a chip under a dash. */}
                  {PART_GROUPS.map((group) => (
                    <td key={group} className={`${cell}`}>
                      <SubStepChips
                        sub={sub}
                        items={items}
                        only={group}
                        outputs={madeSoFar.get(key)?.held}
                        // What this row took that the stock beside it did not have. From the
                        // same walk that fills that column, so the two cannot disagree about
                        // which of them is wrong.
                        missing={onHand.get(key)?.missing}
                        // And what it handled several of in one motion, from the same walk.
                        atOnce={onHand.get(key)?.atOnce}
                        empty={<span className="text-sm text-slate-400">—</span>}
                      />
                    </td>
                  ))}
                  <td className={`${cell}`}>
                    <OutputLabel
                      sub={sub}
                      piece={madeSoFar.get(key)?.own}
                      items={items}
                      held={madeSoFar.get(key)?.held}
                    />
                  </td>
                  <td className={`${cell}`}>
                    <AvailableCell
                      have={onHand.get(key)}
                      items={items}
                      pieces={madeSoFar.get(key)?.held}
                    />
                  </td>
                </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
